import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useAdminMfaGuard } from '@/hooks/useAdminMfaGuard';
import { TwoFactorSetup } from '@/components/auth/TwoFactorSetup';
import { TwoFactorVerify } from '@/components/auth/TwoFactorVerify';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { PageLoader } from '@/components/ui/brand-loader';

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { loading: mfaLoading, mfaRequired, factorId } = useAdminMfaGuard();
  // Local override: if TwoFactorSetup discovers an existing verified factor,
  // switch to verify mode without waiting for a full reload.
  const [overrideVerify, setOverrideVerify] = useState<string | null>(null);

  if (authLoading || (isAuthenticated && mfaLoading)) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // Admin MFA gate — blocks all protected content until MFA is satisfied
  if (mfaRequired === 'setup' && !overrideVerify) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background p-4">
        <TwoFactorSetup
          isRequired
          onComplete={() => {
            toast({
              title: 'Two-factor authentication enabled',
              description: 'Your account is now secured with 2FA.',
            });
            // Force re-check by reloading — the guard will see aal2 on next load
            window.location.reload();
          }}
          onSignOut={async () => {
            await supabase.auth.signOut();
            navigate('/login', { replace: true });
          }}
          onAlreadyEnrolled={(existingFactorId) => {
            // Setup discovered user already has 2FA — switch to verify screen
            setOverrideVerify(existingFactorId);
          }}
        />
      </div>
    );
  }

  if (mfaRequired === 'verify' || overrideVerify) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background p-4">
        <TwoFactorVerify
          factorId={overrideVerify || factorId}
          onComplete={() => {
            // After MFA verification the session is upgraded to aal2 — reload to clear guard
            window.location.reload();
          }}
          onCancel={async () => {
            await supabase.auth.signOut();
            navigate('/login', { replace: true });
          }}
        />
      </div>
    );
  }

  return <>{children}</>;
}
